import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/button";
import { InfoTooltip } from "~/components/info-tooltip";
import { ListItem } from "~/components/list/list-item";
import { Text, textVariants, type TextProps } from "~/components/text";
import { TruncatedText } from "~/components/truncated-text";
import { Input } from "~/components/ui/input";
import { cn } from "~/utils";

export interface SortableListItemProps {
  index: number;
  id: string;
  value: string;
  description?: string;
  infoText?: string;
  subtitle?: string;
  onRemove?: () => void;
  onChange?: (newVal: string) => void;
  onCopy?: () => void;
  disabled?: boolean;
  valueClassName?: string;
  descriptionVariant?: TextProps["variant"];
}

export const SortableListItem: FC<SortableListItemProps> = ({
  value,
  id,
  index,
  infoText,
  description,
  subtitle,
  onRemove,
  onChange,
  onCopy,
  disabled = false,
  valueClassName,
  descriptionVariant = "subtle",
}) => {
  const { t } = useTranslation(["components"]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draftValueState, setDraftValueState] = useState<{
    sourceValue: string;
    value: string;
  } | null>(null);
  const newValue =
    draftValueState?.sourceValue === value ? draftValueState.value : value;

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled: isEditing || disabled });

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (disabled || e.key !== "Enter" || !onChange) {
        return;
      }

      e.preventDefault();
      setIsEditing(false);

      if (newValue && value !== newValue) {
        onChange(newValue);
      }
    },
    [disabled, onChange, newValue, value],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setDraftValueState({ sourceValue: value, value: e.target.value });
    },
    [value],
  );

  const handleValueClick = useCallback(() => {
    if (disabled) {
      return;
    }

    if (onChange) {
      setIsEditing(true);
    } else if (onCopy) {
      onCopy();
    }
  }, [disabled, onChange, onCopy]);

  const onBlur = useCallback(() => {
    if (disabled || !onChange) return;

    setIsEditing(false);

    if (newValue && value !== newValue) {
      onChange(newValue);
    }
  }, [disabled, newValue, value, onChange]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ListItem
        className={cn(isDragging && "opacity-40")}
        testId={`sortable-item-${id}`}
      >
        <div className="flex flex-row w-full items-start" {...attributes}>
          {/** biome-ignore lint/a11y/useFocusableInteractive: dnd-kit puts the role and tabIndex on the parent row through attributes */}
          <div
            {...listeners}
            ref={setActivatorNodeRef}
            className={cn(
              "h-5 min-w-6 flex items-center mt-0.5",
              disabled ? "cursor-default" : "cursor-grab",
            )}
            role="button"
            aria-label={`Drag handle for item ${index + 1}`}
          >
            <GripVertical
              className={cn(
                "hidden h-4 w-4 -ml-1",
                !isEditing && "group-hover:block",
              )}
            />
            <p
              className={cn(
                "block text-sm text-secondary-400",
                !isEditing && "group-hover:hidden",
              )}
            >
              {index + 1}.
            </p>
          </div>

          <div className="flex flex-col flex-1 min-w-0">
            {isEditing ? (
              <Input
                ref={inputRef}
                value={newValue}
                onKeyDown={handleInputKeyDown}
                onChange={handleInputChange}
                onBlur={onBlur}
                autoFocus
                className="text-sm"
              />
            ) : (
              <button
                type="button"
                disabled={disabled || (!onChange && !onCopy)}
                className={cn(
                  "text-sm flex flex-row grow gap-x-2 text-left overflow-hidden min-w-0",
                  disabled ? "cursor-default" : "cursor-pointer",
                )}
                onClick={handleValueClick}
              >
                <TruncatedText
                  text={value}
                  className={cn(
                    textVariants({ variant: "body" }),
                    valueClassName,
                  )}
                />
                <div className="flex items-center min-w-0 flex-1">
                  <TruncatedText
                    text={description}
                    className={textVariants({ variant: descriptionVariant })}
                  />
                </div>
              </button>
            )}
            {subtitle && (
              <Text
                variant="captionItalic"
                className="whitespace-pre-line break-words max-w-80"
              >
                {subtitle}
              </Text>
            )}
          </div>

          <div className="flex items-center gap-1">
            {infoText && <InfoTooltip text={infoText} />}
            {!disabled && !isEditing && onRemove && (
              <Button
                label=""
                variant="ghost"
                icon="x"
                aria-label={t("components:sortableList.remove", { value })}
                onClick={onRemove}
                className="p-0 hidden group-hover:block group-focus-within:block"
                analyticsId="button_remove_final_keyword"
                testId={`remove-${id}`}
              />
            )}
          </div>
        </div>
      </ListItem>
    </div>
  );
};
